import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiFetch from './utils/api';
import { useDrag } from '@use-gesture/react';
import { io } from 'socket.io-client';
import { Tag, MessageSquare, Settings, RotateCw, Menu, EyeOff, CheckSquare, Forward, LayoutGrid, ArrowLeft } from 'lucide-react';

// Auth
import { AuthProvider, useAuth } from './hooks/useAuth';
import { TenantProvider, useTenant } from './hooks/useTenant';
import LoginPage from './components/Auth/LoginPage';
import TenantSelectorPage from './components/Navigation/TenantSelectorPage';

// Components
import SearchBar from './components/Sidebar/SearchBar';
import SedeSelector from './components/Navigation/SedeSelector';
import ConversationList from './components/Sidebar/ConversationList';
import TagFilter from './components/Sidebar/TagFilter';
import ChannelSelector from './components/Sidebar/ChannelSelector';
import InstagramPostsModal from './components/Instagram/InstagramPostsModal';
import ChatHeader from './components/Chat/ChatHeader';
import MessageList from './components/Chat/MessageList';
import MessageInput from './components/Chat/MessageInput';
import TagManager from './components/Tags/TagManager';
import BulkMessageModal from './components/BulkMessaging/BulkMessageModal';
import N8NTestChat from './components/Testing/N8NTestChat';
import EditContactModal from './components/Sidebar/EditContactModal';
import SettingsModal from './components/Settings/SettingsModal';
import ScheduleModal from './components/Modals/ScheduleModal';
import MainLayout from './components/MainLayout';
import AIArea from './components/AI/AIArea';
import Dashboard from './components/Dashboard/Dashboard';
import PaymentsDashboard from './components/Dashboard/PaymentsDashboard';
import BulkActionsBar from './components/Sidebar/BulkActionsBar';
import AdminPanel from './components/Admin/AdminPanel';
import Celebration from './components/UI/Celebration';
import TermsAndConditions from './components/Legal/TermsAndConditions';
import WaTemplates from './components/WaTemplates/WaTemplates';
import WaBulkOfficial from './components/WaTemplates/WaBulkOfficial';
import BulkTracking from './components/WaTemplates/BulkTracking';
import ForwardContactsModal from './components/Chat/ForwardContactsModal';
import TikTokPrototype from './components/TikTok/TikTokPrototype';

// Hooks
import { useConversations } from './hooks/useConversations';
import { useTags } from './hooks/useTags';
import { useSocket } from './hooks/useSocket';

// Styles
import './styles/index.css';

// --- CONFIGURATION ---
let envSocketUrl = process.env.REACT_APP_SOCKET_URL;
if (envSocketUrl && envSocketUrl.includes('localhost') && window.location.hostname !== 'localhost') {
    envSocketUrl = envSocketUrl.replace('localhost', window.location.hostname);
}
let envApiUrl = process.env.REACT_APP_API_URL;
if (envApiUrl && envApiUrl.includes('localhost') && window.location.hostname !== 'localhost') {
    envApiUrl = envApiUrl.replace('localhost', window.location.hostname);
}

const SOCKET_URL = envSocketUrl ||
    (process.env.NODE_ENV === 'production' ? window.location.origin : `http://${window.location.hostname}:4000`);
const API_URL = envApiUrl ||
    (process.env.NODE_ENV === 'production' ? window.location.origin : `http://${window.location.hostname}:4000`);

console.log('ðŸŒ Configured API_URL:', API_URL);
console.log('ðŸ”Œ Configured SOCKET_URL:', SOCKET_URL);

const sameConversationIdentifier = (first, second) => {
    const left = String(first || '').trim();
    const right = String(second || '').trim();
    if (!left || !right) return false;
    if (left === right) return true;

    const leftDigits = left.replace(/\D/g, '');
    const rightDigits = right.replace(/\D/g, '');
    return Boolean(leftDigits && rightDigits && leftDigits === rightDigits);
};

const AuthenticatedApp = () => {
    const { user, logout } = useAuth();


    // Navigation state
    const [activeTab, setActiveTab] = useState('chat');
    const [trackingReturnCampaign, setTrackingReturnCampaign] = useState(null);
    // Temporary conversation list loaded from one campaign tab.
    // null means the sidebar is showing the regular conversation list.
    const [trackingConversationList, setTrackingConversationList] = useState(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // Mobile menu state
    const isMounted = React.useRef(false);

    // UI state
    const [isMobile, setIsMobile] = useState(false);
    const [showSidebar, setShowSidebar] = useState(true);
    const [isRailCollapsed, setIsRailCollapsed] = useState(() => {
        const saved = localStorage.getItem('isRailCollapsed');
        return saved !== null ? JSON.parse(saved) : true;
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [sidebarWidth, setSidebarWidth] = useState(() => {
        const saved = localStorage.getItem('sidebarWidth');
        return saved ? parseInt(saved, 10) : 360;
    });
    const [isResizing, setIsResizing] = useState(false);

    // Filters
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [showUnreadOnly, setShowUnreadOnly] = useState(false);
    const [dateFilter, setDateFilter] = useState(null);
    const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
    const [leadTimeFilter, setLeadTimeFilter] = useState(null);
    const [selectedChannel, setSelectedChannel] = useState('all');

    // Modals
    const [showTagManager, setShowTagManager] = useState(false);
    const [showBulkMessage, setShowBulkMessage] = useState(false);
    const [showN8NTest, setShowN8NTest] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showInstagramPosts, setShowInstagramPosts] = useState(false);
    const [verifyingMessageIds, setVerifyingMessageIds] = useState({});
    const [verificationResult, setVerificationResult] = useState(null);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [scheduleMessage, setScheduleMessage] = useState(null);
    const [draftMessage, setDraftMessage] = useState(null);

    // Forward Message Multi-Select State
    const [isForwardSelectionMode, setIsForwardSelectionMode] = useState(false);
    const [selectedMessagesToForward, setSelectedMessagesToForward] = useState([]);
    const [showForwardContactsModal, setShowForwardContactsModal] = useState(false);

    // Sweep Mode (Escobita)
    const [isSweepMode, setIsSweepMode] = useState(false);
    const [lastSelectedPhone, setLastSelectedPhone] = useState(null);
    const [lastSelectedTimestamp, setLastSelectedTimestamp] = useState(null);

    // Reply Message State
    const [replyToMessage, setReplyToMessage] = useState(null);

    // Editing Message State
    const [editingMessage, setEditingMessage] = useState(null);

    const [showCelebration, setShowCelebration] = useState(false);
    const [agendasCount, setAgendasCount] = useState(0);

    // Multi-selection state
    const [selectedConversationIds, setSelectedConversationIds] = useState([]);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [bulkMessageInitialPhones, setBulkMessageInitialPhones] = useState([]);
    const [bulkMessageInitialMode, setBulkMessageInitialMode] = useState(null);
    const [bulkMessageInitialLeadTime, setBulkMessageInitialLeadTime] = useState(null);

    // Fetch daily agendas count
    useEffect(() => {
        const fetchAgendasCount = async () => {
            try {
                const res = await apiFetch('/api/conversations/agendas-count');
                if (res.ok) {
                    const data = await res.json();
                    setAgendasCount(data.count || 0);
                }
            } catch (err) {
                console.error('Error fetching agendas count:', err);
            }
        };
        fetchAgendasCount();
    }, []);

    // Tags by conversation
    const [tagsByPhone, setTagsByPhone] = useState({});

    const { currentTenant } = useTenant();
    const hideLead = false; // Se mantiene visible para todas las sedes
    const isOfficialTenant = currentTenant?.whatsapp_provider === 'official';

    // Socket connection
    const { socket, isConnected } = useSocket();

    // Re-join general room on connect/reconnect
    useEffect(() => {
        if (socket && isConnected) {
            socket.emit('join-conversations-list');
        }
    }, [socket, isConnected]);

    // Custom hooks


    const {
        conversations,
        selectedConversation,
        messagesByConversation,
        isLoading,
        isLoadingMessages,
        isLoadingMore,
        hasMore,
        aiStatesByPhone,
        selectConversation,
        sendMessage,
        sendFile,
        toggleAI,
        markConversationAsUnread,
        loadMoreConversations,
        searchConversations,
        fetchConversations,
        deleteMessage,
        reactToMessage,
        removeConversation,
        togglePin,
        globalDefaultAi,
        updateConversationLocal,
        messagePaginationByPhone,
        isLoadingOlderMessages,
        loadOlderMessages
    } = useConversations(socket);

    const {
        tags,
        createTag,
        updateTag,
        getConversationTags,
        assignTag,
        removeTag,
        deleteTag,
        fetchTags
    } = useTags();

    // Mobile detection - show sidebar by default on mobile
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 1024;
            setIsMobile(mobile);
            // En mÃ³vil, mostrar sidebar por defecto (se oculta al seleccionar conversaciÃ³n)
            // En desktop, siempre mostrar sidebar
            if (!mobile) {
                setShowSidebar(true);
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Persist sidebar width to localStorage
    useEffect(() => {
        localStorage.setItem('sidebarWidth', sidebarWidth);
    }, [sidebarWidth]);

    useEffect(() => {
        localStorage.setItem('isRailCollapsed', JSON.stringify(isRailCollapsed));
    }, [isRailCollapsed]);

    // Auto-refresh when PWA becomes visible & periodic health check
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log('🔄 App became visible, checking connections...');

                // Reconnect socket if disconnected
                if (socket && !socket.connected) {
                    console.log('🔌 Reconnecting socket...');
                    socket.connect();
                } else if (socket && socket.connected) {
                    // Re-join rooms in case they were lost
                    socket.emit('join-conversations-list');
                }
            }
        };

        // Periodic health check: every 30 seconds, verify socket is in the right rooms
        const healthInterval = setInterval(() => {
            if (socket && socket.connected) {
                // Ensure we're still in the conversations:list room
                socket.emit('join-conversations-list');
            } else if (socket && !socket.connected) {
                console.log('âš ï¸ Health check: socket disconnected, attempting reconnect...');
                socket.connect();
            }
        }, 30000);

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearInterval(healthInterval);
        };
    }, [socket]);

    // Load tags for selected conversation only (not all conversations)
    useEffect(() => {
        if (selectedConversation) {
            const phone = selectedConversation.contact.phone;
            getConversationTags(phone).then(convTags => {
                setTagsByPhone(prev => ({ ...prev, [phone]: convTags }));
            });
        }
    }, [selectedConversation, getConversationTags]);

    // Calculate start/end dates for the backend based on dateFilter string
    const dateRange = useMemo(() => {
        if (!dateFilter) return { start: null, end: null };

        const now = new Date();

        switch (dateFilter) {
            case 'today': {
                const start = new Date(now);
                start.setHours(0, 0, 0, 0);
                const end = new Date(now);
                end.setHours(23, 59, 59, 999);
                return { start: start.toISOString(), end: end.toISOString() };
            }
            case 'yesterday': {
                const start = new Date(now);
                start.setDate(start.getDate() - 1);
                start.setHours(0, 0, 0, 0);
                const end = new Date(now);
                end.setDate(end.getDate() - 1);
                end.setHours(23, 59, 59, 999);
                return { start: start.toISOString(), end: end.toISOString() };
            }
            case 'last7': {
                const start = new Date(now);
                start.setDate(start.getDate() - 7);
                start.setHours(0, 0, 0, 0);
                const end = new Date(now);
                end.setHours(23, 59, 59, 999);
                return { start: start.toISOString(), end: end.toISOString() };
            }
            case 'last30': {
                const start = new Date(now);
                start.setDate(start.getDate() - 30);
                start.setHours(0, 0, 0, 0);
                const end = new Date(now);
                end.setHours(23, 59, 59, 999);
                return { start: start.toISOString(), end: end.toISOString() };
            }
            case 'last90': {
                const start = new Date(now);
                start.setDate(start.getDate() - 90);
                start.setHours(0, 0, 0, 0);
                const end = new Date(now);
                end.setHours(23, 59, 59, 999);
                return { start: start.toISOString(), end: end.toISOString() };
            }
            case 'custom': {
                if (customDateRange.start && customDateRange.end) {
                    const s = new Date(customDateRange.start + 'T00:00:00');
                    const e = new Date(customDateRange.end + 'T23:59:59');
                    return { start: s.toISOString(), end: e.toISOString() };
                }
                return { start: null, end: null };
            }
            default:
                return { start: null, end: null };
        }
    }, [dateFilter, customDateRange]);

    // Primary fetch controller
    // NOTE: dateRange already reacts to dateFilter changes (via useMemo), so we
    // intentionally omit dateFilter here to avoid a double-fetch race condition
    // that causes the conversation list to "jump" when a date filter is applied.
    useEffect(() => {
        if (trackingConversationList !== null) return;

        const activeTagId = selectedTagIds.length === 1 ? selectedTagIds[0] : null;
        const timeoutId = setTimeout(() => {
            fetchConversations(
                1,
                searchQuery,
                false,
                activeTagId,
                dateRange.start,
                dateRange.end,
                showUnreadOnly,
                false,
                leadTimeFilter,
                selectedChannel
            );
        }, 150);
        return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchConversations, activeTab, selectedTagIds, dateRange, searchQuery, showUnreadOnly, leadTimeFilter, selectedChannel, trackingConversationList]);

    // Filter conversations
    const filteredConversations = useMemo(() => {
        let result = conversations;
        if (showUnreadOnly) {
            result = result.filter(conv => conv.unread > 0);
        }
        if (selectedTagIds.length > 0) {
            result = result.filter(conv => {
                const convTags = conv.tags || tagsByPhone[conv.contact.phone] || [];
                return selectedTagIds.some(tagId =>
                    convTags.some(t => t.id === tagId)
                );
            });
        }
        // Client-side date guard: when a date filter is active, socket events can
        // re-insert conversations whose last message is outside the selected range.
        // This filters them out so the list doesn't "jump" with unexpected entries.
        if (dateRange.start || dateRange.end) {
            const rangeStart = dateRange.start ? new Date(dateRange.start).getTime() : null;
            const rangeEnd   = dateRange.end   ? new Date(dateRange.end).getTime()   : null;
            result = result.filter(conv => {
                const ts = conv.rawTimestamp ? new Date(conv.rawTimestamp).getTime() : null;
                if (!ts) return true; // Keep if no timestamp (edge case)
                if (rangeStart && ts < rangeStart) return false;
                if (rangeEnd   && ts > rangeEnd)   return false;
                return true;
            });
        }
        // When a lead time filter is active, only show conversations
        // where the CLIENT was the last to write (lastMessageFromMe !== true)
        // This is a safety net on top of the backend filter for real-time updates
        if (leadTimeFilter) {
            result = result.filter(conv => conv.lastMessageFromMe !== true);
        }
        if (selectedChannel && selectedChannel !== 'all') {
            result = result.filter(conv => (conv.channel || 'whatsapp_evolution') === selectedChannel);
        }
        return result;
    }, [conversations, showUnreadOnly, selectedTagIds, tagsByPhone, leadTimeFilter, dateRange, selectedChannel]);

    // When a campaign tab is loaded into Conversations, keep the campaign's
    // contacts in the sidebar while still allowing its search box to filter
    // that temporary list locally.
    const sidebarConversations = useMemo(() => {
        if (trackingConversationList === null) return filteredConversations;

        const query = searchQuery.trim().toLowerCase();
        if (!query) return trackingConversationList;

        return trackingConversationList.filter(conversation =>
            (conversation.contact?.name || '').toLowerCase().includes(query) ||
            String(conversation.contact?.phone || '').toLowerCase().includes(query) ||
            (conversation.lastMessage || '').toLowerCase().includes(query)
        );
    }, [filteredConversations, trackingConversationList, searchQuery]);

    // Count unread (total visible)
    const unreadCount = useMemo(() => {
        return conversations.filter(c => c.unread > 0).length;
    }, [conversations]);


    // Handlers
    const handleToggleTag = useCallback((tagId) => {
        setSelectedTagIds(prev =>
            prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
        );
    }, []);

    const handleClearFilters = useCallback(() => {
        setSelectedTagIds([]);
        setShowUnreadOnly(false);
        setDateFilter(null);
        setCustomDateRange({ start: '', end: '' });
        setLeadTimeFilter(null);
        setSelectedChannel('all');
    }, []);

    const handleSelectConversation = useCallback((conversation, { preserveTrackingContext = false } = {}) => {
        // Selecting a conversation from the regular chat list starts a new navigation context.
        // Tracking navigation passes preserveTrackingContext explicitly.
        if (!preserveTrackingContext) {
            setTrackingReturnCampaign(null);
            setTrackingConversationList(null);
        }
        if (conversation) {
            setLastSelectedPhone(conversation.contact.phone);
            setLastSelectedTimestamp(conversation.rawTimestamp);
        }
        selectConversation(conversation);
        setReplyToMessage(null);
        if (isMobile) {
            setShowSidebar(false);
        }
    }, [selectConversation, isMobile]);

    const handleReturnToTracking = useCallback(() => {
        if (!trackingReturnCampaign) return;

        selectConversation(null);
        setReplyToMessage(null);
        setEditingMessage(null);
        setTrackingConversationList(null);
        setActiveTab('bulk-tracking');
    }, [selectConversation, trackingReturnCampaign]);

    const handleLoadTrackingConversations = useCallback((recipients, campaign, tab) => {
        const loadedConversations = recipients.map(recipient => {
            const existing = conversations.find(conversation =>
                sameConversationIdentifier(conversation?.contact?.phone, recipient.phone)
            );

            if (existing) return existing;

            return {
                id: recipient.phone,
                contact: {
                    phone: recipient.phone,
                    name: recipient.contact_name || recipient.phone
                },
                lastMessage: recipient.last_message_text || '',
                timestamp: recipient.last_message_timestamp || '',
                rawTimestamp: recipient.last_message_timestamp || null,
                unread: 0,
                status: 'active',
                aiEnabled: globalDefaultAi,
                state: 'ai_active',
                tags: [],
                isPinned: false,
                channel: 'whatsapp_official'
            };
        });

        setTrackingConversationList(loadedConversations);
        setTrackingReturnCampaign({ ...campaign, trackingTab: tab });
        setSearchQuery('');
        selectConversation(null);
        setReplyToMessage(null);
        setEditingMessage(null);
        setShowSidebar(true);
        setActiveTab('chat');
    }, [conversations, globalDefaultAi, selectConversation]);

    // Sweep mode logic: find the one that was above the last processed one
    useEffect(() => {
        if (isSweepMode && !selectedConversation && lastSelectedPhone && activeTab === 'chat') {
            // Find current index of the phone we just left
            const currentIndex = filteredConversations.findIndex(c => c.contact.phone === lastSelectedPhone);

            let targetConv = null;

            if (currentIndex > 0) {
                // Normal case: pick the one right above
                targetConv = filteredConversations[currentIndex - 1];
            } else if (currentIndex === 0) {
                // It's already at the top, maybe it jumped there. 
                // We should try to find the neighbor with a timestamp just newer than our old one.
                if (lastSelectedTimestamp) {
                    const ts = new Date(lastSelectedTimestamp).getTime();
                    // Find conversations with timestamp > ts, pick the one with the smallest such timestamp
                    const candidates = filteredConversations
                        .filter(c => new Date(c.rawTimestamp).getTime() > ts)
                        .sort((a, b) => new Date(a.rawTimestamp).getTime() - new Date(b.rawTimestamp).getTime());

                    if (candidates.length > 0) {
                        targetConv = candidates[0];
                    }
                }
            } else {
                // Not found (maybe archived). 
                // We can't do much without more context, but let's try to pick something at the same index?
                // For now, let's just do nothing if not found.
            }

            if (targetConv) {
                const timeout = setTimeout(() => {
                    handleSelectConversation(targetConv);
                }, 300);
                return () => clearTimeout(timeout);
            }
        }
    }, [selectedConversation, isSweepMode, lastSelectedPhone, lastSelectedTimestamp, filteredConversations, activeTab, handleSelectConversation]);

    const handleSendMessage = useCallback(async (message) => {
        console.log('ðŸ‘¤ Sending message as user:', user);
        if (selectedConversation) {
            if (editingMessage) {
                try {
                    const response = await apiFetch(`/api/messages/${editingMessage.whatsapp_id || editingMessage.id}`, {
                        method: 'PUT',
                        body: JSON.stringify({ text: message })
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.error || 'Error al editar mensaje');
                    }

                    setEditingMessage(null);
                } catch (error) {
                    console.error('Error editing message:', error);
                    alert(`Error al editar: ${error.message}`);
                }
                return;
            }

            sendMessage(
                selectedConversation.contact.phone,
                message,
                selectedConversation.contact.name,
                {
                    agentId: user?.id,
                    agentName: user?.name,
                    replyTo: replyToMessage?.whatsapp_id || replyToMessage?.id,
                    replyToFull: replyToMessage ? {
                        id: replyToMessage.whatsapp_id || replyToMessage.id,
                        text: replyToMessage.text,
                        sender: replyToMessage.sender_name || (replyToMessage.sender === 'agent' ? user?.name : 'Cliente')
                    } : null
                }
            );
            setReplyToMessage(null);
        }
    }, [selectedConversation, sendMessage, user, replyToMessage, editingMessage]);

    const handleSendFile = useCallback(async (file, caption) => {
        if (!selectedConversation) return;
        try {
            await sendFile(
                selectedConversation.contact.phone,
                file,
                caption,
                selectedConversation.contact.name,
                {
                    agentId: user?.id,
                    agentName: user?.name,
                    replyTo: replyToMessage?.whatsapp_id || replyToMessage?.id,
                    replyToFull: replyToMessage ? {
                        id: replyToMessage.whatsapp_id || replyToMessage.id,
                        text: replyToMessage.text,
                        sender: replyToMessage.sender_name || (replyToMessage.sender === 'agent' ? user?.name : 'Cliente')
                    } : null
                }
            );
            setReplyToMessage(null);
        } catch (error) {
            // Error is shown inline in the conversation bubble — no alert needed
            console.error('Error in App handleSendFile:', error);
        }
    }, [selectedConversation, sendFile, user, replyToMessage]);

    const handleBulkSend = useCallback(async (phonesOrFilters, message, mediaFiles = null, mediaOptions = null) => {
        let recipients = [];
        let filters = null;

        // Determine if we're sending explicit recipients or filters
        if (Array.isArray(phonesOrFilters)) {
            recipients = phonesOrFilters.map(phone => {
                const conv = conversations.find(c => c.contact.phone === phone);
                return { phone, name: conv?.contact.name || 'Unknown' };
            });
        } else {
            // It's a filter object
            filters = phonesOrFilters;
            console.log('Using server-side filters:', filters);
        }

        // Process all media items (upload local files, keep server urls)
        let finalMediaItems = [];

        // If mediaOptions is passed (e.g. from forwarding)
        if (mediaOptions && (mediaOptions.mediaUrl || mediaOptions.mediaUrls)) {
            const urls = mediaOptions.mediaUrls || (Array.isArray(mediaOptions.mediaUrl) ? mediaOptions.mediaUrl : [mediaOptions.mediaUrl]);
            const types = mediaOptions.mediaTypes || (Array.isArray(mediaOptions.mediaType) ? mediaOptions.mediaType : [mediaOptions.mediaType]);
            
            urls.forEach((url, idx) => {
                if (url) {
                    finalMediaItems.push({
                        url,
                        type: types[idx] || (Array.isArray(types) ? 'image' : types) || 'image'
                    });
                }
            });
        }

        // If mediaFiles is passed (from upload/file selector)
        if (mediaFiles) {
            const filesArray = Array.isArray(mediaFiles) ? mediaFiles : [mediaFiles];
            for (const item of filesArray) {
                if (item instanceof File) {
                    const formData = new FormData();
                    formData.append('file', item);
                    const uploadRes = await apiFetch('/api/upload?folder=bulk', { method: 'POST', body: formData });
                    if (!uploadRes.ok) throw new Error('Error subiendo archivo para envío masivo');
                    const { file: uploadedFile } = await uploadRes.json();
                    finalMediaItems.push({ url: uploadedFile.url, type: uploadedFile.type });
                } else if (item && item.file instanceof File) {
                    const formData = new FormData();
                    formData.append('file', item.file);
                    const uploadRes = await apiFetch('/api/upload?folder=bulk', { method: 'POST', body: formData });
                    if (!uploadRes.ok) throw new Error('Error subiendo archivo para envío masivo');
                    const { file: uploadedFile } = await uploadRes.json();
                    finalMediaItems.push({ url: uploadedFile.url, type: uploadedFile.type });
                } else if (item && item.url) {
                    finalMediaItems.push({ url: item.url, type: item.type });
                }
            }
        }

        const payloadBody = {
            recipients: filters ? [] : recipients,
            filters: filters || undefined,
            message,
            agentId: user?.id,
            agentName: user?.name,
            agent_id: user?.id,
            agent_name: user?.name
        };

        if (finalMediaItems.length > 0) {
            payloadBody.mediaUrl = finalMediaItems.map(item => item.url);
            payloadBody.mediaType = finalMediaItems.map(item => item.type);
        }

        console.log(`📤 Sending bulk message via /api/bulk-send`, payloadBody);
        const response = await apiFetch('/api/bulk-send', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payloadBody)
        });

        if (!response.ok) throw new Error('Error al iniciar envío masivo');
        const result = await response.json();
        console.log('✅ Bulk send initiated:', result);
        return result;
    }, [conversations, user]);

    const handleForwardMessage = useCallback((message) => {
        setIsForwardSelectionMode(true);
        setSelectedMessagesToForward(prev => {
            if (prev.some(m => m.id === message.id)) return prev;
            return [...prev, message];
        });
    }, []);

    const toggleForwardSelection = useCallback((message) => {
        setSelectedMessagesToForward(prev => {
            const exists = prev.some(m => m.id === message.id);
            if (exists) {
                const next = prev.filter(m => m.id !== message.id);
                if (next.length === 0) setIsForwardSelectionMode(false);
                return next;
            } else {
                return [...prev, message];
            }
        });
    }, []);

    const cancelForwardSelection = useCallback(() => {
        setIsForwardSelectionMode(false);
        setSelectedMessagesToForward([]);
    }, []);

    const handleForwardSend = useCallback(async (phones, messages) => {
        for (const msg of messages) {
            if (msg.mediaUrl || msg.media_url) {
                await handleBulkSend(phones, msg.text || '', null, {
                    mediaUrl: msg.mediaUrl || msg.media_url,
                    mediaType: msg.mediaType || msg.media_type
                });
            } else {
                await handleBulkSend(phones, msg.text || '');
            }
            await new Promise(r => setTimeout(r, 500));
        }
        setIsForwardSelectionMode(false);
        setSelectedMessagesToForward([]);
    }, [handleBulkSend]);

    const handleReplyMessage = useCallback((message) => {
        setReplyToMessage(message);
    }, []);

    const handleStartBulkSend = useCallback(({ phones = [], mode = null, leadTime = null }) => {
        setBulkMessageInitialPhones(phones);
        setBulkMessageInitialMode(mode);
        setBulkMessageInitialLeadTime(leadTime);
        setShowBulkMessage(true);
    }, []);

    const handleCreateTag = useCallback(async (name, color) => {
        const newTag = await createTag(name, color);
        return newTag;
    }, [createTag]);

    const handleUpdateTag = useCallback(async (tagId, name, color) => {
        const updatedTag = await updateTag(tagId, name, color);

        // Also update local copy in tagsByPhone if we have it
        setTagsByPhone(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(phone => {
                const tags = next[phone];
                if (tags.some(t => t.id === tagId)) {
                    next[phone] = tags.map(t => t.id === tagId ? updatedTag : t);
                }
            });
            return next;
        });

        return updatedTag;
    }, [updateTag]);

    const handleDeleteTag = useCallback(async (tagId) => {
        const success = await deleteTag(tagId);
        if (success) {
            // Eliminar de los contactos en memoria
            setTagsByPhone(prev => {
                const next = { ...prev };
                Object.keys(next).forEach(phone => {
                    next[phone] = next[phone].filter(t => t.id !== tagId);
                });
                return next;
            });
        }
        return success;
    }, [deleteTag]);

    const handleAssignTag = useCallback(async (phone, tagId) => {
        await assignTag(phone, tagId, user?.id);
        const updatedTags = await getConversationTags(phone);
        setTagsByPhone(prev => ({ ...prev, [phone]: updatedTags }));
        
        fetchTags(); // Actualiza conteo global de etiquetas
        
        // If it was the "Agendar" tag, the lead_time is cleared on the server
        const tag = tags.find(t => t.id === tagId);
        if (tag && tag.name.toLowerCase() === 'agendar') {
            updateConversationLocal(phone, { leadTime: null, tags: updatedTags });
            setShowCelebration(true); // Trigger celebration!
            setAgendasCount(prev => prev + 1); // Increment local counter
        } else {
            updateConversationLocal(phone, { tags: updatedTags });
        }
    }, [assignTag, getConversationTags, updateConversationLocal, user, tags, fetchTags]);

    const handleRemoveTag = useCallback(async (phone, tagId) => {
        const tag = tags.find(t => t.id === tagId);
        await removeTag(phone, tagId);
        const updatedTags = await getConversationTags(phone);
        setTagsByPhone(prev => ({ ...prev, [phone]: updatedTags }));
        
        fetchTags(); // Actualiza conteo global de etiquetas
        
        if (tag && tag.name.toLowerCase() === 'agendar') {
            // Restore AI active state locally if un-scheduling
            updateConversationLocal(phone, { tags: updatedTags, conversationState: 'ai_active' });
            setAgendasCount(prev => Math.max(0, prev - 1));
        } else {
            updateConversationLocal(phone, { tags: updatedTags });
        }
    }, [removeTag, getConversationTags, updateConversationLocal, tags, fetchTags]);

    const [conversationToTag, setConversationToTag] = useState(null);

    const handleMouseDown = (e) => {
        if (isMobile) return;
        setIsResizing(true);
    };

    const handleMouseMove = useCallback((e) => {
        if (!isResizing || isMobile) return;
        window.requestAnimationFrame(() => {
            if (!isResizing) return;
            const newWidth = Math.max(300, Math.min(1000, e.clientX));
            setSidebarWidth(newWidth);
        });
    }, [isResizing, isMobile]);

    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
    }, []);

    useEffect(() => {
        if (isResizing) {
            document.body.classList.add('is-resizing');
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        } else {
            document.body.classList.remove('is-resizing');
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.body.classList.remove('is-resizing');
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    const handleOpenTagManager = useCallback((conversation, e) => {
        if (e) e.stopPropagation();
        setConversationToTag(conversation);
        setShowTagManager(true);
    }, []);

    const handleOpenTagManagerGlobal = useCallback(() => {
        setConversationToTag(null);
        setShowTagManager(true);
    }, []);

    const currentMessages = selectedConversation
        ? messagesByConversation[selectedConversation.contact.phone] || []
        : [];

    const targetConversation = conversationToTag || selectedConversation;
    const currentConversationTags = targetConversation
        ? tagsByPhone[targetConversation.contact.phone] || []
        : [];

    const handleMarkUnread = useCallback(async (phone) => {
        await markConversationAsUnread(phone);
        selectConversation(null);
        if (isMobile) {
            setShowSidebar(true);
        }
    }, [markConversationAsUnread, selectConversation, isMobile]);

    // Exponer fetchConversations globalmente para que el Service Worker o el componente Pull-to-Refresh puedan usarlo
    useEffect(() => {
        window.fetchConversations = fetchConversations;
    }, [fetchConversations]);

    // Multi-selection handlers
    const enterSelectionMode = useCallback((id) => {
        setSelectedConversationIds([id]);
        setIsSelectionMode(true);
    }, []);

    const toggleConversationSelection = useCallback((id) => {
        setSelectedConversationIds(prev => {
            if (prev.includes(id)) {
                const next = prev.filter(i => i !== id);
                if (next.length === 0) setIsSelectionMode(false);
                return next;
            } else {
                return [...prev, id];
            }
        });
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedConversationIds([]);
        setIsSelectionMode(false);
    }, []);

    const selectAllConversations = useCallback(() => {
        const allIds = filteredConversations.map(c => c.id);
        setSelectedConversationIds(allIds);
    }, [filteredConversations]);

    const handleBulkDelete = useCallback(async () => {
        if (!window.confirm(`Â¿EstÃ¡s seguro de eliminar ${selectedConversationIds.length} conversaciones?`)) return;

        // Note: For a real bulk operation, we should have a backend /bulk-delete
        // For now, we'll loop or use the existing removeConversation correctly
        let successCount = 0;
        for (const id of selectedConversationIds) {
            const conv = filteredConversations.find(c => c.id === id);
            if (conv) {
                try {
                    const phone = conv.contact.phone;
                    // Existing logic for single delete
                    let apiUrl = typeof process !== 'undefined' && process.env?.REACT_APP_API_URL
                        ? process.env.REACT_APP_API_URL
                        : window.location.hostname !== 'localhost'
                            ? 'https://largebotinterfaz-production-5b38.up.railway.app'
                            : 'http://localhost:4000';
                    if (apiUrl.endsWith('/')) apiUrl = apiUrl.slice(0, -1);

                    const res = await apiFetch(`/api/conversations/${encodeURIComponent(phone)}`, {
                        method: 'DELETE'
                    });

                    if (res.ok) {
                        removeConversation(phone);
                        successCount++;
                    }
                } catch (err) {
                    console.error(`Error deleting ${id}:`, err);
                }
            }
        }
        alert(`Se eliminaron ${successCount} conversaciones.`);
        clearSelection();
    }, [selectedConversationIds, filteredConversations, removeConversation, clearSelection]);

    const handleBulkTag = useCallback(() => {
        // Find a representative conversation (or just use null) to open manager
        // We'll modify TagManager to handle multiple phones later if needed
        // For now, let's just use the first selected one to pick tags
        const firstId = selectedConversationIds[0];
        const conv = filteredConversations.find(c => c.id === firstId);
        if (conv) {
            setConversationToTag({
                ...conv,
                isBulk: true,
                phones: selectedConversationIds.map(id => filteredConversations.find(c => c.id === id)?.contact.phone).filter(Boolean)
            });
            setShowTagManager(true);
        }
    }, [selectedConversationIds, filteredConversations]);

    const handleBulkMessage = useCallback(() => {
        const phones = selectedConversationIds.map(id => filteredConversations.find(c => c.id === id)?.contact.phone).filter(Boolean);
        setBulkMessageInitialPhones(phones);
        setBulkMessageInitialMode('manual');
        setShowBulkMessage(true);
    }, [selectedConversationIds, filteredConversations]);

    const handleTabChange = useCallback((tab) => {
        if (tab === 'settings') {
            setShowSettings(true);
        } else if (tab === 'bulk') {
            setShowBulkMessage(true);
        } else {
            console.log('Changing tab to:', tab);
            setActiveTab(tab);
            // If switching back to chat, ensure sidebar is visible on desktop
            if (tab === 'chat' && !isMobile) {
                setShowSidebar(true);
            }
        }
    }, [isMobile]);



    const handleStartNewChat = async (phone) => {
        try {
            const res = await apiFetch('/api/conversations/start-new', {
                method: 'POST',
                body: JSON.stringify({ phone })
            });

            const data = await res.json();
            console.log("StartNewChat Response:", data);

            if (!res.ok) {
                throw new Error(data.error || data.message || 'El nÃºmero no se encuentra registrado en WhatsApp o la API fallÃ³.');
            }

            const rawConv = data.conversation;
            if (!rawConv) {
                throw new Error('La API no devolviÃ³ los datos de la conversaciÃ³n.');
            }

            // Normalize structure for useConversations (needs contact object)
            const newConv = {
                ...rawConv,
                contact: {
                    phone: rawConv.phone,
                    name: rawConv.contact_name || rawConv.phone
                }
            };

            // Select conversation (will trigger open chat)
            await fetchConversations();

            selectConversation(newConv);
            if (isMobile) setShowSidebar(false);

        } catch (error) {
            console.error('Error starting new chat:', error);
            alert(`No se pudo iniciar la conversaciÃ³n con este nÃºmero.\n\nMotivo: ${error.message}`);
        }
    };

    // Swipe Back Gesture for Mobile
    const bindSwipe = useDrag(({ movement: [mx], cancel, active }) => {
        if (!isMobile || !selectedConversation) return;
        // Swipe right to go back
        if (mx > 100 && !isResizing) {
            cancel();
            setShowSidebar(true);
            if (searchQuery) {
                setSearchQuery('');
                fetchConversations(1, '');
            }
            selectConversation(null); // Optional: clear selection to fully reset
        }
    }, {
        axis: 'x',
        filterTaps: true,
        rubberband: true
    });

    const handleMessageDelete = useCallback(async (message) => {
        if (!window.confirm('Â¿EstÃ¡s seguro de que quieres eliminar este mensaje?')) return;

        try {
            await deleteMessage(message.id, selectedConversation?.contact.phone);
        } catch (error) {
            console.error('Error deleting message:', error);
            alert('Error al eliminar mensaje');
        }
    }, [deleteMessage, selectedConversation]);

    const handleMessageReact = useCallback((message, emoji) => {
        // Optimistic update supported by hook
        reactToMessage(message.id, emoji, selectedConversation?.contact.phone);
    }, [reactToMessage, selectedConversation]);

    const handleVerifyPayment = useCallback(async (message) => {
        console.log('🔍 [handleVerifyPayment] Iniciando verificación de pago...');
        console.log('🔍 [handleVerifyPayment] Mensaje recibido:', message);
        
        const rawId = message.id || message.whatsapp_id;
        console.log('🔍 [handleVerifyPayment] ID crudo extraído:', rawId);
        
        if (!rawId) {
            console.error('❌ [handleVerifyPayment] Error: El mensaje no tiene un ID válido.');
            return;
        }

        // Clean ID suffix (e.g. @c.us) to match the ID format in MessageList
        const messageId = String(rawId).split('@')[0].trim();
        console.log('🔍 [handleVerifyPayment] ID limpio para el cargador:', messageId);

        setVerifyingMessageIds(prev => {
            const next = { ...prev, [messageId]: true };
            console.log('🔍 [handleVerifyPayment] Nuevo mapa de carga:', next);
            return next;
        });

        try {
            console.log('📡 [handleVerifyPayment] Enviando request a trigger-verify para ID:', rawId);
            const response = await apiFetch('/api/payments/trigger-verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ messageId: rawId }) // Send raw ID to the backend query
            });

            console.log('📡 [handleVerifyPayment] Respuesta recibida de trigger-verify. Status:', response.status);
            const data = await response.json();
            console.log('📡 [handleVerifyPayment] Datos de la respuesta:', data);

            setVerifyingMessageIds(prev => {
                const next = { ...prev };
                delete next[messageId];
                console.log('🔍 [handleVerifyPayment] Quitando del cargador. Nuevo mapa:', next);
                return next;
            });

            if (!response.ok) {
                throw new Error(data.error || 'Error al procesar la verificación');
            }

            if (data.success) {
                console.log('✅ [handleVerifyPayment] Éxito del webhook. Result:', data.n8nResult);
                
                let resultPayload = data.n8nResult;
                if (Array.isArray(resultPayload) && resultPayload.length > 0) {
                    resultPayload = resultPayload[0];
                }

                // If n8n stripped multipleMatches/payments, we bypass it by calling /verify ourselves
                // since we now have the OCR amount from the webhook response!
                if (resultPayload.ocr && resultPayload.ocr.amount) {
                    try {
                        const directVerifyRes = await apiFetch('/api/payments/verify', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                amount: resultPayload.ocr.amount,
                                reference: resultPayload.ocr.reference,
                                conversation_phone: null
                            })
                        });
                        
                        if (directVerifyRes.ok) {
                            const directData = await directVerifyRes.json();
                            console.log('📡 [handleVerifyPayment] Direct /verify response:', directData);
                            // Merge the direct data (which has all fields) with the OCR data
                            resultPayload = {
                                ...resultPayload,
                                ...directData
                            };
                        }
                    } catch (e) {
                        console.error('Error in direct verify fallback:', e);
                    }
                }

                setVerificationResult({
                    ...resultPayload,
                    messageId: rawId
                });
            }
        } catch (error) {
            console.error('❌ [handleVerifyPayment] Error capturado:', error);
            setVerifyingMessageIds(prev => {
                const next = { ...prev };
                delete next[messageId];
                return next;
            });
            alert(`❌ Error al verificar pago: ${error.message}`);
        }
    }, []);

    const handleScheduleMessage = useCallback((message) => {
        setScheduleMessage(message);
        setShowScheduleModal(true);
    }, []);

    const handleScheduleSubmit = useCallback((scheduleDetails, rawFormData) => {
        // En vez de enviar automáticamente, rellenamos el textarea
        setDraftMessage(scheduleDetails);
    }, []);

    return (
        <MainLayout
            activeTab={activeTab}
            onTabChange={handleTabChange}
            isMobile={isMobile}
            onLogout={logout}
            hideHeader={isMobile && activeTab === 'chat'} // Hide default header on mobile chat
            isMenuOpen={isMobileMenuOpen}
            onMenuOpen={() => setIsMobileMenuOpen(true)}
            onMenuClose={() => setIsMobileMenuOpen(false)}
            isCollapsed={isRailCollapsed}
            onToggleCollapse={() => setIsRailCollapsed(!isRailCollapsed)}
            user={user}
            isOfficialTenant={isOfficialTenant}
        >
            <div className="app-container" style={{
                display: activeTab === 'chat' ? 'flex' : 'none',
                height: '100%',
                width: '100%',
                maxWidth: '100vw',
                overflowX: 'hidden'
            }}>
                {/* Sidebar */}
                <div
                    className={`sidebar ${isMobile && showSidebar ? 'open' : ''}`}
                    style={!isMobile ? { width: `${sidebarWidth}px` } : {}}
                >
                    {/* Sidebar Header */}
                    <div className="sidebar-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                            <button
                                className="btn btn-icon"
                                onClick={() => isMobile ? setIsMobileMenuOpen(true) : setIsRailCollapsed(!isRailCollapsed)}
                                style={{
                                    color: 'var(--color-primary)',
                                    backgroundColor: 'rgba(7,94,84,0.1)',
                                    borderRadius: '8px',
                                    padding: '6px',
                                    marginRight: '8px'
                                }}
                                title={isMobile ? "Menú" : (isRailCollapsed ? "Mostrar Navegación" : "Ocultar Navegación")}
                            >
                                <Menu className="w-5 h-5" />
                            </button>

                            <span style={{ fontWeight: 700, fontSize: 'var(--font-size-lg)', color: 'var(--color-gray-800)' }}>Chat</span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexShrink: 0 }}>
                            {/* Connection status */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                                <span className={`connection-dot ${isConnected ? 'connected' : 'disconnected'}`} />
                                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)' }}>
                                    {isConnected ? 'Conectado' : 'Desconectado'}
                                </span>
                            </div>

                            {/* Sweep Mode (Escobita) */}
                            <button
                                className={`btn btn-icon ${isSweepMode ? 'active' : ''}`}
                                onClick={() => setIsSweepMode(!isSweepMode)}
                                title={isSweepMode ? 'Desactivar Barrido' : 'Activar Barrido (Escobita)'}
                                style={{
                                    backgroundColor: isSweepMode ? 'rgba(7,94,84,0.1)' : 'transparent',
                                    color: isSweepMode ? 'var(--color-primary)' : 'var(--color-gray-500)',
                                    padding: '6px'
                                }}
                            >
                                <BroomIcon className="w-5 h-5" style={{ transform: isSweepMode ? 'rotate(-45deg)' : 'none', transition: 'transform 0.3s' }} />
                                {isSweepMode && (
                                    <span style={{
                                        position: 'absolute',
                                        top: '0',
                                        right: '0',
                                        width: '8px',
                                        height: '8px',
                                        backgroundColor: 'var(--color-primary)',
                                        borderRadius: '50%',
                                        border: '1px solid white'
                                    }} />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Sede Selector */}
                    <div style={{ padding: '0 4px 12px 4px' }}>
                        <SedeSelector />
                    </div>

                    {/* Search */}
                    <div style={{ paddingRight: '12px' }}>
                        <SearchBar
                            value={searchQuery}
                            onChange={setSearchQuery}
                        />
                    </div>

                    {/* Channels Filter */}
                    <div style={{ paddingRight: '12px' }}>
                        <ChannelSelector
                            selectedChannel={selectedChannel}
                            onSelectChannel={setSelectedChannel}
                            onOpenInstagramPosts={() => setShowInstagramPosts(true)}
                        />
                    </div>

                    {/* Tag Filter */}
                    <TagFilter
                        tags={tags}
                        selectedTagIds={selectedTagIds}
                        onToggleTag={handleToggleTag}
                        onClearFilter={handleClearFilters}
                        showUnreadOnly={showUnreadOnly}
                        onToggleUnreadOnly={() => setShowUnreadOnly(!showUnreadOnly)}
                        dateFilter={dateFilter}
                        onDateFilterChange={setDateFilter}
                        customDateRange={customDateRange}
                        onCustomDateRangeChange={setCustomDateRange}
                        unreadCount={unreadCount}
                        onRefresh={fetchConversations}
                        isLoading={isLoading}
                        onCreateTag={createTag}
                        onUpdateTag={handleUpdateTag}
                        onDeleteTag={handleDeleteTag}
                        userRole={user?.role}
                        leadTimeFilter={leadTimeFilter}
                        onLeadTimeFilterChange={setLeadTimeFilter}
                        onStartBulkSend={handleStartBulkSend}
                    />


                    {trackingConversationList !== null && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#f5f3ff', borderBottom: '1px solid #ddd6fe', color: '#5b21b6' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    Campaña: {trackingReturnCampaign?.campaign_name || trackingReturnCampaign?.template_name || 'Seguimiento'}
                                </div>
                                <div style={{ fontSize: 10, marginTop: 2 }}>
                                    {trackingConversationList.length} conversaciones cargadas
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setTrackingConversationList(null);
                                    setTrackingReturnCampaign(null);
                                }}
                                style={{ border: 'none', background: 'transparent', color: '#7c3aed', cursor: 'pointer', fontSize: 11, fontWeight: 700, padding: '4px 6px' }}
                            >
                                Limpiar
                            </button>
                        </div>
                    )}

                    {/* Conversation List */}
                    <ConversationList
                        conversations={sidebarConversations}
                        selectedId={selectedConversation?.id}
                        searchQuery={searchQuery}
                        aiStatesByPhone={aiStatesByPhone}
                        tagsByPhone={tagsByPhone}
                        isLoading={trackingConversationList === null && isLoading}
                        isLoadingMore={trackingConversationList === null && isLoadingMore}
                        hasMore={trackingConversationList === null && hasMore}
                        onSelect={(conversation) => handleSelectConversation(conversation, {
                            preserveTrackingContext: trackingConversationList !== null
                        })}
                        onTagClick={handleOpenTagManager}
                        onRefresh={() => {
                            const activeTagId = selectedTagIds.length === 1 ? selectedTagIds[0] : null;
                            fetchConversations(1, searchQuery, false, activeTagId, dateRange.start, dateRange.end, showUnreadOnly, false, leadTimeFilter, selectedChannel);
                        }}
                        onLoadMore={() => {
                            const activeTagId = selectedTagIds.length === 1 ? selectedTagIds[0] : null;
                            loadMoreConversations(activeTagId, dateRange.start, dateRange.end, showUnreadOnly, leadTimeFilter, selectedChannel);
                        }}
                        onStartNewChat={handleStartNewChat}
                        onDelete={removeConversation}
                        onTogglePin={togglePin}
                        globalDefaultAi={globalDefaultAi}
                        isSelectionMode={isSelectionMode}
                        selectedIds={selectedConversationIds}
                        onToggleSelection={toggleConversationSelection}
                        onEnterSelectionMode={enterSelectionMode}
                    />

                    <BulkActionsBar
                        selectedCount={selectedConversationIds.length}
                        onClear={clearSelection}
                        onDelete={handleBulkDelete}
                        onTag={handleBulkTag}
                        onMessage={handleBulkMessage}
                        onSelectAll={selectAllConversations}
                    />

                    {/* Resize handle - Desktop only */}
                    {!isMobile && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                right: -6,
                                width: '12px',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'ew-resize',
                                zIndex: 10,
                                transition: 'all var(--transition-fast)'
                            }}
                            onMouseDown={handleMouseDown}
                        >
                            {/* Visual indicator */}
                            <div
                                style={{
                                    width: '4px',
                                    height: '80px',
                                    backgroundColor: isResizing ? 'var(--color-primary)' : 'var(--color-gray-300)',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    boxShadow: isResizing ? '0 0 8px rgba(0,0,0,0.2)' : '0 0 4px rgba(0,0,0,0.1)',
                                    transition: 'all var(--transition-fast)',
                                    opacity: 0.7
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.opacity = '1';
                                    e.currentTarget.style.backgroundColor = 'var(--color-primary)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isResizing) {
                                        e.currentTarget.style.opacity = '0.7';
                                        e.currentTarget.style.backgroundColor = 'var(--color-gray-300)';
                                    }
                                }}
                            >
                                {/* Arrows icon */}
                                <div style={{
                                    color: 'white',
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    userSelect: 'none',
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)'
                                }}>
                                    âŸ·
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Chat Area */}
                <div
                    className="chat-container"
                    style={isMobile && showSidebar ? { display: 'none' } : {}}
                >
                    {selectedConversation ? (
                        <>
                            <ChatHeader
                                conversation={selectedConversation}
                                aiEnabled={aiStatesByPhone[selectedConversation.contact.phone] ?? globalDefaultAi}
                                onToggleAI={toggleAI}
                                onMarkUnread={handleMarkUnread}
                                onBack={() => {
                                    if (isSweepMode) {
                                        selectConversation(null);
                                    } else {
                                        setShowSidebar(true);
                                    }

                                    if (searchQuery) {
                                        setSearchQuery('');
                                        fetchConversations(1, ''); // Reload all
                                    }
                                }}
                                isMobile={isMobile}
                                isSweepMode={isSweepMode}
                                onBackToTracking={trackingReturnCampaign ? handleReturnToTracking : undefined}
                                trackingCampaignName={trackingReturnCampaign?.campaign_name || trackingReturnCampaign?.template_name}
                                onNameUpdated={(phone, newName) => updateConversationLocal(phone, { contact: { name: newName } })}
                                agendasCount={agendasCount}
                            />

                            {/* Tags bar */}
                            <div style={{
                                padding: 'var(--space-2) var(--space-4)',
                                backgroundColor: 'var(--color-white)',
                                borderBottom: '1px solid var(--color-gray-200)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                flexWrap: 'wrap'
                            }}>
                                {(tagsByPhone[selectedConversation.contact.phone] || []).map(tag => (
                                    <span
                                        key={tag.id}
                                        className="tag tag-small"
                                        style={{ backgroundColor: tag.color, color: '#fff' }}
                                    >
                                        {tag.name}
                                    </span>
                                ))}
                                <button
                                    className="btn btn-icon"
                                    onClick={() => {
                                        setConversationToTag(selectedConversation);
                                        setShowTagManager(true);
                                    }}
                                    style={{
                                        padding: '2px 8px',
                                        fontSize: 'var(--font-size-xs)',
                                        backgroundColor: 'var(--color-gray-100)'
                                    }}
                                >
                                    <Tag className="w-3 h-3" />
                                    <span style={{ marginLeft: '4px' }}>Etiquetas</span>
                                </button>

                                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {isSweepMode && (
                                        <button
                                            className="btn btn-icon"
                                            onClick={() => setIsSweepMode(false)}
                                            title="Desactivar Barrido"
                                            style={{
                                                color: 'var(--color-primary)',
                                                backgroundColor: 'rgba(7,94,84,0.1)',
                                                borderRadius: '8px',
                                            }}
                                        >
                                            <BroomIcon className="w-5 h-5" />
                                        </button>
                                    )}
                                    <button
                                        className="btn btn-icon"
                                        onClick={() => handleMarkUnread(selectedConversation.contact.phone)}
                                        title="Marcar como no leÃ­do"
                                        style={{
                                            color: 'var(--color-primary)',
                                        }}
                                    >
                                        <EyeOff className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>


                            <div {...bindSwipe()} style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, touchAction: 'none' }}>
                                <MessageList
                                    conversationId={selectedConversation?.contact?.phone || selectedConversation?.id}
                                    messages={currentMessages}
                                    isLoading={isLoadingMessages}
                                    onForward={handleForwardMessage}
                                    onReact={handleMessageReact}
                                    onDelete={handleMessageDelete}
                                    onReply={handleReplyMessage}
                                    onEdit={setEditingMessage}
                                    onSchedule={handleScheduleMessage}
                                    onPhoneClick={handleStartNewChat}
                                    onVerifyPayment={handleVerifyPayment}
                                    verifyingMessageIds={verifyingMessageIds}
                                    onLoadOlder={selectedConversation ? () => loadOlderMessages(selectedConversation.contact.phone) : undefined}
                                    hasMoreOlder={selectedConversation ? (messagePaginationByPhone[selectedConversation.contact.phone]?.hasMore ?? false) : false}
                                    isLoadingOlder={isLoadingOlderMessages}
                                    isForwardSelectionMode={isForwardSelectionMode}
                                    selectedMessagesToForward={selectedMessagesToForward}
                                    onToggleMessageSelection={toggleForwardSelection}
                                />
                            </div>

                            {/* Floating footer for forward selection */}
                            {isForwardSelectionMode && (
                                <div style={{
                                    padding: '12px 16px',
                                    background: '#f0fdf4',
                                    borderTop: '1px solid #bbf7d0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    zIndex: 10
                                }}>
                                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#15803d' }}>
                                        {selectedMessagesToForward.length} mensaje{selectedMessagesToForward.length !== 1 && 's'} seleccionado{selectedMessagesToForward.length !== 1 && 's'}
                                    </span>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button 
                                            onClick={cancelForwardSelection}
                                            style={{ padding: '8px 12px', border: '1px solid #bbf7d0', background: 'white', borderRadius: '8px', color: '#15803d', fontSize: '13px', cursor: 'pointer', fontWeight: 600 }}
                                        >
                                            Cancelar
                                        </button>
                                        <button 
                                            onClick={() => setShowForwardContactsModal(true)}
                                            style={{ padding: '8px 12px', border: 'none', background: '#25d366', borderRadius: '8px', color: 'white', fontSize: '13px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}
                                        >
                                            <Forward size={14} /> Reenviar
                                        </button>
                                    </div>
                                </div>
                            )}

                            <MessageInput
                                onSend={handleSendMessage}
                                onSendFile={handleSendFile}
                                disabled={false}
                                isMobile={isMobile}
                                replyToMessage={replyToMessage}
                                onCancelReply={() => setReplyToMessage(null)}
                                editingMessage={editingMessage}
                                onCancelEdit={() => setEditingMessage(null)}
                                draftMessage={draftMessage}
                                onDraftConsumed={() => setDraftMessage(null)}
                                currentPhone={selectedConversation?.contact?.phone}
                                isOfficialTenant={isOfficialTenant}
                            />
                        </>
                    ) : (
                        <div className="flex-center" style={{ height: '100%', flexDirection: 'column', gap: 'var(--space-4)' }}>
                            <MessageSquare className="w-16 h-16" style={{ color: 'var(--color-gray-300)' }} />
                            <div style={{ textAlign: 'center' }}>
                                <h2 style={{
                                    fontSize: 'var(--font-size-xl)',
                                    fontWeight: 600,
                                    color: 'var(--color-gray-700)',
                                    marginBottom: 'var(--space-2)'
                                }}>
                                    Selecciona una conversación
                                </h2>
                                <p style={{ color: 'var(--color-gray-500)' }}>
                                    Elige una conversación de la lista para comenzar
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div >



            {/* Modals - Moved outside app-container to be visible in all tabs */}
            <TagManager
                isOpen={showTagManager}
                onClose={() => {
                    setShowTagManager(false);
                    setConversationToTag(null);
                }}
                tags={tags}
                conversationPhone={targetConversation?.contact.phone}
                conversationTags={currentConversationTags}
                onCreateTag={handleCreateTag}
                onUpdateTag={handleUpdateTag}
                onAssignTag={handleAssignTag}
                onRemoveTag={handleRemoveTag}
                onMarkUnread={handleMarkUnread}
                isBulk={targetConversation?.isBulk}
                bulkPhones={targetConversation?.phones}
            />

            <BulkMessageModal
                isOpen={showBulkMessage}
                onClose={() => {
                    setShowBulkMessage(false);
                    setBulkMessageInitialPhones([]);
                    setBulkMessageInitialMode(null);
                    setBulkMessageInitialLeadTime(null);
                }}
                conversations={conversations}
                tags={tags}
                tagsByPhone={tagsByPhone}
                onSend={handleBulkSend}
                socket={socket}
                initialSelectedPhones={bulkMessageInitialPhones}
                initialSelectionMode={bulkMessageInitialMode}
                initialSelectedLeadTime={bulkMessageInitialLeadTime}
            />

            <ForwardContactsModal
                isOpen={showForwardContactsModal}
                onClose={() => setShowForwardContactsModal(false)}
                conversations={conversations}
                onSend={handleForwardSend}
                messagesToForward={selectedMessagesToForward}
            />

            <N8NTestChat
                isOpen={showN8NTest}
                onClose={() => setShowN8NTest(false)}
            />

            <SettingsModal
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
            />

            <InstagramPostsModal
                isOpen={showInstagramPosts}
                onClose={() => setShowInstagramPosts(false)}
                socket={socket}
            />

            <Celebration 
                isVisible={showCelebration} 
                onClose={() => setShowCelebration(false)} 
            />

            <ScheduleModal
                isOpen={showScheduleModal}
                onClose={() => {
                    setShowScheduleModal(false);
                    setScheduleMessage(null);
                }}
                initialData={
                    scheduleMessage ? {
                        messageText: scheduleMessage.text,
                        contactName: selectedConversation?.contact?.name
                    } : null
                }
                onSubmit={handleScheduleSubmit}
            />

            {activeTab === 'dashboard' && <Dashboard isMobile={isMobile} />}
            {activeTab === 'payments_dashboard' && <PaymentsDashboard isMobile={isMobile} />}
            {activeTab === 'ai' && <AIArea isMobile={isMobile} user={user} isGlobalOnly={false} />}
            {activeTab === 'ai_global' && <AIArea isMobile={isMobile} user={user} isGlobalOnly={true} />}
            {activeTab === 'admin' && <AdminPanel isMobile={isMobile} onNavigateTab={(tab) => setActiveTab(tab)} />}
            {activeTab === 'wa-templates' && <WaTemplates onBulkSend={(tpl) => setActiveTab('wa-bulk')} />}
            {activeTab === 'wa-bulk' && <WaBulkOfficial conversations={conversations} tags={tags} />}
            {activeTab === 'bulk-tracking' && <BulkTracking
                initialCampaign={trackingReturnCampaign}
                onInitialCampaignOpened={() => setTrackingReturnCampaign(null)}
                onLoadConversations={handleLoadTrackingConversations}
                onOpenConversation={(phone, campaign, recipient) => {
                    const conv = conversations.find(c => sameConversationIdentifier(c?.contact?.phone, phone));
                    const conversationToOpen = conv || {
                        id: phone,
                        contact: {
                            phone,
                            name: recipient?.contact_name || recipient?.name || phone
                        },
                        lastMessage: recipient?.last_message_text || '',
                        rawTimestamp: recipient?.last_message_timestamp || null,
                        unread: 0,
                        aiEnabled: globalDefaultAi,
                        state: 'ai_active'
                    };
                    setTrackingReturnCampaign(campaign || null);
                    setSearchQuery('');
                    handleSelectConversation(conversationToOpen, { preserveTrackingContext: true });
                    setActiveTab('chat');
                }}
            />}

            {verificationResult && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.65)',
                    backdropFilter: 'blur(8px)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'inherit',
                    padding: '16px'
                }}>
                    <style>{`
                        @keyframes pulse-ring {
                            0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
                            70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
                            100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
                        }
                        @keyframes success-pop {
                            0% { transform: scale(0.8); opacity: 0; }
                            100% { transform: scale(1); opacity: 1; }
                        }
                    `}</style>
                    <div style={{
                        backgroundColor: 'var(--color-bg-paper, #ffffff)',
                        padding: '32px',
                        borderRadius: '20px',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        width: '100%',
                        maxWidth: '480px',
                        color: 'var(--color-gray-800, #1f2937)',
                        boxSizing: 'border-box',
                        animation: 'success-pop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                    }}>
                        {/* ICON HEADER */}
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                            {verificationResult.matched ? (
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '50%',
                                    backgroundColor: verificationResult.usedReference ? 'rgba(220, 38, 38, 0.1)' : (verificationResult.alreadyVerified ? 'rgba(59, 130, 246, 0.1)' : 'rgba(16, 185, 129, 0.1)'),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: verificationResult.usedReference ? '#dc2626' : (verificationResult.alreadyVerified ? '#3b82f6' : '#10b981'),
                                    fontSize: '32px'
                                }}>
                                    {verificationResult.usedReference ? '🛑' : (verificationResult.alreadyVerified ? 'ℹ' : '✓')}
                                </div>
                            ) : (
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#ef4444',
                                    fontSize: '32px',
                                    animation: 'pulse-ring 2s infinite'
                                }}>
                                    ⚠️
                                </div>
                            )}
                        </div>

                        {/* TITLE & MESSAGE */}
                        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                            <h3 style={{
                                fontSize: '20px',
                                fontWeight: 700,
                                margin: 0,
                                color: verificationResult.usedReference ? '#dc2626' : (verificationResult.multipleMatches ? '#f59e0b' : (verificationResult.alreadyVerified ? '#3b82f6' : (verificationResult.matched ? '#10b981' : '#ef4444')))
                            }}>
                                {verificationResult.usedReference ? '¡Comprobante Ya Utilizado!' : (verificationResult.multipleMatches ? '¡Múltiples Coincidencias!' : (verificationResult.alreadyVerified ? '¡Pago Ya Verificado!' : (verificationResult.matched ? '¡Pago Coincide!' : 'Sin Coincidencia Bancaria')))}
                            </h3>
                            <p style={{
                                fontSize: '14px',
                                color: 'var(--color-gray-500, #6b7280)',
                                marginTop: '8px',
                                lineHeight: '1.5',
                                padding: '0 8px'
                            }}>
                                {verificationResult.usedReference 
                                    ? 'Hemos identificado que esta misma referencia de comprobante ya fue validada previamente. No se puede reutilizar.'
                                    : (verificationResult.multipleMatches
                                        ? 'Hemos encontrado varios pagos con el mismo valor en las últimas 24 horas. Por favor, selecciona el correcto.'
                                        : (verificationResult.alreadyVerified 
                                            ? 'Este movimiento de transferencia bancaria ya ha sido verificado y registrado anteriormente.'
                                            : (verificationResult.matched 
                                                ? 'Hemos encontrado un movimiento pendiente en la cuenta que coincide con el valor extraído del comprobante.'
                                                : 'No se encontró coincidencia en las notificaciones bancarias de las últimas 24 horas. Por favor, verifica de nuevo.')))
                                }
                            </p>
                        </div>

                        {/* DETAILS CARD */}
                        <div style={{
                            backgroundColor: 'var(--color-bg-base, #f9fafb)',
                            border: `1px solid ${verificationResult.alreadyVerified ? 'rgba(59, 130, 246, 0.2)' : (verificationResult.matched ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)')}`,
                            borderRadius: '12px',
                            padding: '16px',
                            marginBottom: '28px',
                            fontSize: '14px'
                        }}>
                            {/* OCR Info */}
                            <div style={{
                                borderBottom: '1px dashed #e5e7eb',
                                paddingBottom: '12px',
                                marginBottom: '12px'
                            }}>
                                <span style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--color-gray-400, #9ca3af)', textTransform: 'uppercase', marginBottom: '4px' }}>Leído del Comprobante (IA)</span>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <span>Valor Leído:</span>
                                    <strong style={{ fontSize: '15px' }}>${parseFloat(verificationResult.ocr?.amount || 0).toLocaleString('es-CO')}</strong>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Ref. Comprobante:</span>
                                    <strong>{verificationResult.ocr?.reference || 'No detectada'}</strong>
                                </div>
                            </div>

                            {/* Bank Match Info */}
                            {verificationResult.matched && verificationResult.multipleMatches ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <span style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#f59e0b', textTransform: 'uppercase', marginBottom: '4px' }}>
                                        Múltiples Pagos Encontrados
                                    </span>
                                    <p style={{ fontSize: '13px', color: 'var(--color-gray-600)', margin: 0 }}>Selecciona el pago correcto a verificar:</p>
                                    <div style={{ maxHeight: '250px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {verificationResult.payments.map((p) => (
                                            <label key={p.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', padding: '8px', border: verificationResult.payment?.id === p.id ? '2px solid #3b82f6' : '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: verificationResult.payment?.id === p.id ? 'rgba(59, 130, 246, 0.05)' : '#fff' }}>
                                                <input type="radio" name="payment_selection" checked={verificationResult.payment?.id === p.id} onChange={() => setVerificationResult({...verificationResult, payment: p, alreadyVerified: p.status === 'verified'})} style={{ marginTop: '4px' }} />
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <strong style={{ fontSize: '13px' }}>{p.payer_name || 'Desconocido'}</strong>
                                                        <span style={{ fontSize: '12px', color: p.status === 'verified' ? '#3b82f6' : '#10b981', fontWeight: 600 }}>{p.status === 'verified' ? 'Ya Verificado' : 'Pendiente'}</span>
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>{p.bank || 'Banco'} - {new Date(p.payment_date).toLocaleTimeString('es-CO', {hour: '2-digit', minute:'2-digit'})} ({new Date(p.payment_date).toLocaleDateString('es-CO')})</div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                verificationResult.matched && verificationResult.payment && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <span style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: verificationResult.alreadyVerified ? '#3b82f6' : '#10b981', textTransform: 'uppercase', marginBottom: '4px' }}>
                                            {verificationResult.alreadyVerified ? 'Registrado y Verificado' : 'Registrado en el Banco'}
                                        </span>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>👤 Pagador:</span>
                                            <strong>{verificationResult.payment.payer_name || 'Desconocido'}</strong>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>🏦 Banco:</span>
                                            <strong>{verificationResult.payment.bank || 'Bancolombia'}</strong>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>📅 Fecha Notificación:</span>
                                            <strong>{new Date(verificationResult.payment.payment_date).toLocaleTimeString('es-CO', {hour: '2-digit', minute:'2-digit'})} ({new Date(verificationResult.payment.payment_date).toLocaleDateString('es-CO')})</strong>
                                        </div>
                                    </div>
                                )
                            )}
                        </div>

                        {/* ACTIONS */}
                        <div style={{ display: 'flex', gap: '12px' }}>
                            {verificationResult.matched && !verificationResult.alreadyVerified ? (
                                <>
                                    <button
                                        onClick={() => setVerificationResult(null)}
                                        style={{
                                            flex: 1,
                                            padding: '12px 16px',
                                            borderRadius: '10px',
                                            border: '1px solid #e5e7eb',
                                            backgroundColor: '#ffffff',
                                            color: '#374151',
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            cursor: 'pointer',
                                            transition: 'background-color 0.2s'
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={async () => {
                                            const payment = verificationResult.payment;
                                            setVerificationResult(null);
                                            document.body.style.cursor = 'wait';
                                            try {
                                                const patchRes = await apiFetch(`/api/payments/${payment.id}/status`, {
                                                    method: 'PATCH',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify({
                                                        status: 'verified',
                                                        notes: `Verificado por agente tras OCR de comprobante. Ref cliente: ${verificationResult.ocr?.reference || 'N/A'}.`,
                                                        verified_by: user?.name || 'Agente'
                                                    })
                                                });
                                                document.body.style.cursor = 'default';
                                                if (!patchRes.ok) {
                                                    const patchData = await patchRes.json();
                                                    throw new Error(patchData.error || 'No se pudo confirmar la verificación');
                                                }
                                                alert(`✅ ¡Pago Verificado y Registrado con éxito!\n\nAsociado a la conversación.`);
                                            } catch (err) {
                                                document.body.style.cursor = 'default';
                                                alert(`❌ Error al confirmar verificación: ${err.message}`);
                                            }
                                        }}
                                        disabled={!verificationResult.payment}
                                        style={{
                                            flex: 1,
                                            padding: '12px 16px',
                                            borderRadius: '10px',
                                            border: 'none',
                                            backgroundColor: !verificationResult.payment ? '#9ca3af' : '#10b981',
                                            color: '#ffffff',
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            cursor: !verificationResult.payment ? 'not-allowed' : 'pointer',
                                            transition: 'background-color 0.2s'
                                        }}
                                    >
                                        Verificar Pago
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={() => setVerificationResult(null)}
                                    style={{
                                        flex: 1,
                                        padding: '12px 16px',
                                        borderRadius: '10px',
                                        border: 'none',
                                        backgroundColor: '#374151',
                                        color: '#ffffff',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s',
                                        textAlign: 'center'
                                    }}
                                >
                                    Entendido, Cerrar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </MainLayout>
    );
};

// Root Component with Auth Provider
// Custom Broom Icon for Sweep Mode
const BroomIcon = ({ className, style }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        style={style}
    >
        <path d="m13 11 9-9" />
        <path d="M14.6 12.6c.8.8.9 2.1.2 3L10 22l-8-8 6.4-4.8c.9-.7 2.2-.6 3 .2Z" />
        <path d="m6.8 10.4 6.8 6.8" />
        <path d="m5 17 1.4-1.4" />
    </svg>
);

const App = () => {
    // Public, data-free prototype route for demonstrating the planned TikTok flow.
    // It intentionally bypasses CRM authentication and never calls the backend.
    if (window.location.pathname.replace(/\/$/, '') === '/prototipo-tiktok') {
        return <TikTokPrototype />;
    }

    return (
        <AuthProvider>
            <TenantProvider>
                <AppContent />
            </TenantProvider>
        </AuthProvider>
    );
};

// Auth Guard Content
const AppContent = () => {
    const { isAuthenticated, loading, user } = useAuth();
    const { currentTenant } = useTenant();

    useEffect(() => {
        if (currentTenant?.slug?.includes('marketing')) {
            document.body.classList.add('theme-marketing');
        } else {
            document.body.classList.remove('theme-marketing');
        }
    }, [currentTenant]);

    const isTermsPage = window.location.pathname === '/terminosycondiciones' || 
                        window.location.pathname === '/terminosycondiciones/';

    if (isTermsPage) {
        return <TermsAndConditions />;
    }

    if (loading) {
        return (
            <div style={{
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f3f4f6',
                color: '#4b5563',
                fontSize: '1.25rem'
            }}>
                Cargando...
            </div>
        );
    }

    if (!isAuthenticated) return <LoginPage />;

    // Wait for tenant identification if authenticated
    // This prevents "Sede no especificada" errors from triggered effects on mount
    const hasTenants = user?.tenants?.length > 0 || user?.role === 'SUPER_ADMIN';

    if (isAuthenticated && hasTenants && !currentTenant) {
        // If Super Admin, show the selection cards
        if (user.role === 'SUPER_ADMIN') {
            return <TenantSelectorPage />;
        }

        // For regular users, show loading while the default tenant is auto-selected
        return (
            <div style={{
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: '24px',
                background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                color: '#11ab9c',
                fontFamily: 'system-ui, -apple-system, sans-serif'
            }}>
                <div style={{
                    position: 'relative',
                    width: '80px',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        border: '4px solid rgba(17, 171, 156, 0.1)',
                        borderRadius: '50%',
                        borderTopColor: '#11ab9c',
                        animation: 'spin 1s linear infinite'
                    }} />
                    <RotateCw className="w-8 h-8" style={{ animation: 'reverse-spin 2s linear infinite' }} />
                </div>
                <div style={{ textAlign: 'center' }}>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>Cargando su Sede</h2>
                    <p style={{ color: '#6b7280', marginTop: '8px' }}>Configurando su espacio de trabajo...</p>
                </div>
                <style>{`
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes reverse-spin { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
                `}</style>
            </div>
        );
    }

    return <AuthenticatedApp />;
};

export default App;
